import React from 'react';
import { NavLink, Location } from 'react-router-dom';

import Tag from 'antd/es/tag';

import dummyAvatar from '@common/react/images/avatar.jpg';
import { useMobileView } from '@common/react/hooks/useMobileView';

import { DoctorPortal } from '@app/objects/DoctorPortal';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';
import DoctorAvatar from '@app/components/UI/DoctorCard/DoctorAvatar';
import { getLocationName } from '@app/utils/Utils';
import SocialIcons from '@app/components/UI/SocialIcons/SocialIcons';
import Experience from '@app/components/UI/Experience/Experience';

interface Props {
	doctor: DoctorPortal;
	location: Location;
	withoutNavMenu?: boolean;
}

const navMenu = [
	{
		title: 'About',
		check: (doctor: DoctorPortal) => !!doctor?.portalContentEn,
		id: 'about',
	},
	{
		title: 'Locations',
		check: (doctor: DoctorPortal) => doctor.locations.length > 0,
		id: 'locations',
	},
	{
		title: 'Book Online',
		check: (doctor: DoctorPortal) => true,
		id: 'bookOnline',
		className: 'show-mobile show-mobile__inline-block',
	},
	{
		title: 'Reviews',
		check: (doctor: DoctorPortal) => doctor.totalReviewCount > 0,
		id: 'reviews',
	},
];

const DoctorMenu: React.FC<Props> = ({
	doctor: content, withoutNavMenu = false, location,
}) => {
	const ref = React.useRef<any>(null);
	const isMobile = useMobileView(768);
	const [showOrigin, setShowOrigin] = React.useState(false);

	React.useEffect(() => {
		setShowOrigin(isMobile);
	}, [isMobile]);
	const avatar = (showOrigin ? content.originalAvatar : content.avatar) || dummyAvatar;

	return (
		<div className="mb20 px15 main-content-block" ref={ref}>
			<div className="row">
				<div className="left-pull">
					<div className="avatar">
						<DoctorAvatar
							key={(!showOrigin || !avatar) ? 'default' : 'origin'}
							hideWhenLoading
							withLink={false}
							alt={content.nameEn}
							src={avatar}
						/>
					</div>
					{content.socialMedias ? <SocialIcons
						className="doctor-portal__icons"
						socialMedias={content.socialMedias}
					/> : null}
				</div>
				<div className="text-start left-pull main-info">
					{content && <div>
						<h1 className="doctor-name">{content.nameEn}</h1>
					</div>}
					<Experience experience={content.experience} />
					<div className="doctor-menu-content">
						<div>
							<div className="pull-left">
								{content.specialties?.length > 0 && content.specialties.filter((item) => item.specialty)
									.map((item) => <SpecialtyTag
										key={`specialty${item.id}`}
										tagClassName="title__second"
										specialty={item.specialty}
									/>)}
								{content.availableServices?.length > 0 && content.availableServices?.filter((item) => item)
									.map((item) =>
										(<React.Fragment key={item.id}>
											{item.path
												? <NavLink to={`/procedures/${item.path}`}>
													<Tag color="purple" key={item.id} className="title__second">
														{item.name}
													</Tag>
												</NavLink>
												: <Tag color="purple" key={item.id} className="title__second">
													{item.name}
												</Tag>
											}
										</React.Fragment>))}
							</div>
							{content.locations?.length > 0
								? <h2 className="title__third pull-right doctor-location inline-select-item inline-select-item_sm">
									{content.locations[0].portalPathEn
										? <NavLink to={`/clinics/${content.locations[0].portalPathEn}`}>
											<i className="fa fa-map-marker" />
											{' '}
											{getLocationName(content.locations[0], false, true)}
										</NavLink>
										: content.locations[0].nameEn}
								</h2> : null}
						</div>
						{content && !withoutNavMenu
						&& <nav className="doctor-portal__nav pull-left">
							<ul>
								{navMenu.filter((item) => item.check(content)).map((item) =>
									<li key={item.id} className={item.className}>
										<a href={`${location?.pathname}#${item.id}`}>{item.title}</a>
									</li>)
								}
							</ul>
						</nav>}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DoctorMenu;
